import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConfirmStep from "../../components/VMRS/ExploreTasking/ConfirmStep";
import Step1Comp from "../../components/VMRS/ExploreTasking/Step1";
import DynamicStepRenderer from "../../components/VMRS/ExploreTasking/components/DynamicStepRenderer";
import { useWorkflow } from "../../components/VMRS/ExploreTasking/hook/useWorkflow";
import { UniversalMap } from "../../components/VMRS/MapAdapter/UniversalMap";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import { RootState } from "../../store/store";
import { Spin } from "antd";
import
{
   setDrawingAOIMode,
   setFeatureVisibility,
   showAllAOI,
} from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { useTranslation } from "react-i18next";

import { clearAOIFeatures } from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { clearAllLayers } from "../../components/VMRS/MapAdapter/store/slices/layerSlice";

import { clearFeatures as clearDrawFeatures, setDrawingMode } from "../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { clearSegments as clearMeasureSegments, setMeasureMode } from "../../components/VMRS/MapAdapter/store/slices/measureSlice";
import useToggle from "../../hooks/useToggle";
import CommonComponents from "../../components/Commons";
import ProviderCard from "../../components/VMRS/ExploreTasking/components/ProviderCard";
import { getSatellitesAsync } from "../../store/reducers/orderSlice";

// pages/ExploreTaskingPage.tsx
const ExploreTaskingPage = () =>
{
   const { t } = useTranslation();
   let allProviders = useSelector(
      (state: RootState) => state.appCommonSlice.allProviders,
   );

   const [selectedProvider, setSelectedProvider] = useState(null);
   const [selectedSatellite, setSelectedSatellite] = useState<any>(null);
   const [mode, setMode] = useState<"public" | "workflow" | "confirm">("public"); // mode pulic is step provider, workflow is step by step form schema driven
   const [loading, setLoading] = useState(false);

   const [satellites, setSatellites] = useState<any[]>([]);

   const allAOI = useSelector((state: RootState) => state.aoi.features);

   let activeFeatureId = useSelector(
      (state: RootState) => state.aoi.activeFeatureId,
   );

   const dispatch = useDispatch();

   const {
      open: openConfirm,
      toggle: toggleConfirm,
      shouldRender: shouldConfirm,
   } = useToggle();

   const {
      currentStep,
      currentStepIndex,
      totalSteps,
      formData,
      setFormData,
      next,
      back,
      submit,
      fieldMeta,
      stepFieldMap,
      workflow,
      setCurrentStepIndex,
      conFirmSubmit,
   } = useWorkflow(
      selectedProvider,
      setLoading,
      toggleConfirm,
      selectedSatellite,
   );

   const handleNext = () =>
   {
      if (currentStepIndex + 1 === totalSteps - 1)
      {
         setMode("confirm");
      } else
      {
         next();
      }
   };

   const handleBack = () =>
   {
      if (mode === "confirm")
      {
         setMode("workflow");
      } else if (currentStepIndex === 0)
      {
         setMode("public");
      } else
      {
         back();
      }
   };

   useEffect(() =>
   {
      setSatellites([]);
      if (!selectedProvider)
      {
         return;
      }

      handleGetSatellites();
   }, [selectedProvider]);

   const handleGetSatellites = async () =>
   {
      try
      {
         setLoading(true);
         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            getSatellitesAsync({
               providerCode: selectedProvider,
               skip: 0,
               limit: 100,
            }),
         ).unwrap();

         setSatellites(res?.data?.data);
         setSelectedSatellite(
            res?.data?.data?.length ? [res.data.data[0].name] : [], // note: backend yêu cầu truyền tên satellite thay vì id
         );
      } catch (error)
      {
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      if (!allAOI?.length) return;

      if (mode !== "public")
      {
         allAOI.forEach((feat) =>
         {
            const isActive = feat.id === activeFeatureId;

            if (feat.visible !== isActive)
            {
               dispatch(
                  setFeatureVisibility({
                     id: feat.id,
                     visible: isActive,
                  }),
               );
            }
         });

         dispatch(setDrawingAOIMode("NONE"));
      } else
      {
         dispatch(showAllAOI());
      }
   }, [mode, activeFeatureId]);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearDrawFeatures());


      dispatch(clearMeasureSegments());
      dispatch(clearAllLayers());

      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      return () =>
      {
         handleResetMap();
      };
   }, []);

   return (
      <ContentLayoutManagement
         header={<HeaderContent breadcrumb={[{ title: t("Explore Tasking") }]} />}
      >
         <div className="h-full flex bg-[#0f172a] text-white">
            <div className="w-5/12 flex flex-col border-r border-[#26324a]">
               <Spin spinning={loading} size="large" className="absolute z-10">
                  {mode === "public" && (
                     <Step1Comp
                        allProviders={allProviders}
                        selectedProvider={selectedProvider}
                        selectedSatellite={selectedSatellite}
                        setSelectedProvider={(val?: any) => setSelectedProvider(val)}
                        setSelectedSatellite={(val?: any) => setSelectedSatellite(val)}
                        onOrder={() => setMode("workflow")}
                        totalSteps={totalSteps}
                        loading={loading}
                        setLoading={setLoading}
                        workflow={workflow}
                        satellites={satellites}
                     />
                  )}

                  {mode === "workflow" && (
                     <>
                        <DynamicStepRenderer
                           step={currentStep}
                           formData={formData}
                           setFormData={setFormData}
                           currentStepIndex={currentStepIndex}
                           totalSteps={totalSteps}
                           next={handleNext}
                           back={handleBack}
                           submit={submit}
                           onBackToSelect={() => setMode("public")}
                           fieldMeta={fieldMeta}
                           stepFieldMap={stepFieldMap}
                           workflow={workflow}
                           setCurrentStepIndex={setCurrentStepIndex}
                           satellites={satellites}
                           selectedSatellite={selectedSatellite}
                           allProviders={allProviders}
                           selectedProvider={selectedProvider}
                        />
                     </>
                  )}
                  {mode === "confirm" && (
                     <ConfirmStep
                        formData={formData}
                        fieldMeta={fieldMeta}
                        stepFieldMap={stepFieldMap}
                        workflow={workflow}
                        onBack={handleBack}
                        onSubmit={submit}
                        currentStepIndex={currentStepIndex}
                        totalSteps={totalSteps}
                     />
                  )}

                  {shouldConfirm && (
                     <CommonComponents.ModalUI
                        title={t("Confirm")}
                        open={openConfirm}
                        toggle={toggleConfirm}
                        handleConfirm={conFirmSubmit}
                     >
                        <p>
                           {t("Are you sure you want to create this request order?")}
                        </p>
                     </CommonComponents.ModalUI>
                  )}
               </Spin>
            </div>

            <div className="w-7/12 flex-1 relative">
               <UniversalMap
                  mapType="maplibre"
                  showEngine={false}
                  showDraw={false}
                  showDrawAOI={mode === "public"}
                  showMeasure={false}
                  view={{
                     center: [108.23653393802581, 16.1039225743402],
                     zoom: 5,
                  }}
                  showBaseMap={true}
                  showMapControl={true}

                  allowMoveActive={mode === "public"}
               />
            </div>
         </div>
      </ContentLayoutManagement>
   );
};

export default ExploreTaskingPage;
